/**
 * Modal Component
 * Reusable modal dialog for forms and confirmations
 */

import React, { useEffect, useId, useRef } from 'react';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { ModalPortal } from '../Modals/ModalPortal';

/**
 * Escape handling for every overlay dialog, owned in one place so the order of handlers is fixed.
 *
 * Overlays register in the order they open; the last one is the top-most and the only one Escape
 * can close. Each Escape goes through three rules, in this order:
 *  1. A dialog nested in the top-most overlay (for example the shared Agent editor opened from a
 *     config dialog) owns the key: its own "Close" button is clicked. If it has no Close button,
 *     nothing else happens.
 *  2. A popup control with focus (an open dropdown, combobox or menu) owns the key. It closes itself,
 *     and the overlay stays open.
 *  3. Otherwise the top-most overlay closes. When focus is inside it, the key is first offered to the
 *     focused control. The overlay closes on the bubble pass only if no control called preventDefault.
 *     When focus is elsewhere (for example on the canvas), the overlay closes on the capture pass, so
 *     the canvas never sees the key and does not clear the selection behind the dialog.
 *
 * A handled Escape is marked defaultPrevented. Global shortcuts check that flag, and isAnyModalOpen()
 * tells them whether an overlay is open.
 */
interface EscapeOverlay {
  root: () => HTMLElement | null;
  close: () => void;
}

const escapeOverlays: EscapeOverlay[] = [];

/** Escape events a popup control claimed on the capture pass. The bubble pass must leave them alone. */
const popupClaimedEvents = new WeakSet<Event>();

/**
 * Controls that close their own popup on Escape. While one of these has focus, the key belongs to it.
 * Only popup triggers are listed. A plain aria-expanded element, such as an accordion header, is not.
 */
const POPUP_OWNS_ESCAPE =
  '[role="combobox"][aria-expanded="true"], [aria-haspopup][aria-expanded="true"], .af-searchable-select--open';

function topOverlay(): EscapeOverlay | undefined {
  return escapeOverlays[escapeOverlays.length - 1];
}

/** The Close button of the dialog that should close first while the host is open, if there is one. */
function nestedCloseButton(root: HTMLElement, active: HTMLElement | null): HTMLElement | null {
  // Focus inside a nested dialog decides it first. This also covers a nested dialog that is portaled
  // outside the host's DOM. With focus elsewhere in the host, use a nested dialog rendered inside it.
  // If several are open, the last one in document order is closed first.
  const focusedNested = active?.closest<HTMLElement>('[aria-modal="true"]');
  const nested = focusedNested && focusedNested !== root
    ? focusedNested
    : Array.from(root.querySelectorAll<HTMLElement>('[aria-modal="true"]')).pop();
  return nested?.querySelector<HTMLElement>('[aria-label="Close"]') ?? null;
}

function hasNestedDialog(root: HTMLElement): boolean {
  return root.querySelector('[aria-modal="true"]') !== null;
}

function handleEscapeCapture(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || e.defaultPrevented) return;
  const overlay = topOverlay();
  const root = overlay?.root();
  if (!overlay || !root) return;
  const active = document.activeElement as HTMLElement | null;

  // Rule 1: a nested dialog owns the key. Escape closes it, never the host.
  if (hasNestedDialog(root) || (active?.closest('[aria-modal="true"]') ?? root) !== root) {
    const close = nestedCloseButton(root, active);
    if (close) {
      e.preventDefault();
      e.stopPropagation();
      close.click();
    }
    return;
  }

  // Rule 2: an open popup with focus closes itself. Mark the event so the bubble pass does not
  // close the overlay as well.
  if (active?.closest(POPUP_OWNS_ESCAPE)) {
    popupClaimedEvents.add(e);
    return;
  }

  // Rule 3, focus inside the overlay: the focused control gets the key first (see handleEscapeBubble).
  if (active && root.contains(active)) return;

  // Rule 3, focus elsewhere: the overlay takes the key before the focused element sees it.
  e.preventDefault();
  e.stopPropagation();
  overlay.close();
}

function handleEscapeBubble(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || e.defaultPrevented || popupClaimedEvents.has(e)) return;
  const overlay = topOverlay();
  const root = overlay?.root();
  if (!overlay || !root) return;
  const active = document.activeElement;
  // Runs after the focused control's own handlers. A control that handled Escape has marked the event
  // defaultPrevented, which leaves the overlay open.
  if (!active || !root.contains(active) || hasNestedDialog(root)) return;
  e.preventDefault();
  overlay.close();
}

/**
 * Adds an overlay to the stack and returns a function that removes it. The window listeners are
 * installed while any overlay is open and removed when the last one closes.
 */
function registerEscapeOverlay(overlay: EscapeOverlay): () => void {
  if (escapeOverlays.length === 0) {
    window.addEventListener('keydown', handleEscapeCapture, true);
    window.addEventListener('keydown', handleEscapeBubble);
  }
  escapeOverlays.push(overlay);
  return () => {
    const index = escapeOverlays.indexOf(overlay);
    if (index >= 0) escapeOverlays.splice(index, 1);
    if (escapeOverlays.length === 0) {
      window.removeEventListener('keydown', handleEscapeCapture, true);
      window.removeEventListener('keydown', handleEscapeBubble);
    }
  };
}

/**
 * Registers an overlay for Escape handling while `isOpen` is true (see the rules above). Use this for
 * any overlay that isn't built on <Modal> but is still a dialog. `dialogRef` must point at the
 * overlay's own dialog element, so its root is not mistaken for a nested dialog.
 */
export function useTopmostEscape(isOpen: boolean, onClose: () => void, dialogRef: React.RefObject<HTMLElement>): void {
  // Read the latest onClose without re-registering the overlay on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    return registerEscapeOverlay({
      root: () => dialogRef.current,
      close: () => onCloseRef.current(),
    });
  }, [isOpen, dialogRef]);
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** True while any Modal or useTopmostEscape overlay is open. Global shortcuts use this to stand down. */
export function isAnyModalOpen(): boolean {
  return escapeOverlays.length > 0;
}

/**
 * Focus handling for any dialog: moves focus into it on open, keeps Tab/Shift+Tab inside it
 * (a nested aria-modal dialog owns focus while open), and restores focus to the opener on close.
 */
export function useDialogFocus(dialogRef: React.RefObject<HTMLElement>, isOpen: boolean): void {
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    const opener = document.activeElement as HTMLElement | null;

    const focusables = () =>
      dialog ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : [];
    // Defer the initial focus until after the dialog has mounted; focusing during the same
    // commit can be overridden by content that mounts afterwards. setTimeout (not
    // requestAnimationFrame) so it also runs when the window is in the background.
    const timer = window.setTimeout(() => (focusables()[0] ?? dialog)?.focus(), 0);

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialog) return;
      const active = document.activeElement as HTMLElement | null;
      const inner = active?.closest('[aria-modal="true"]');
      if (inner && inner !== dialog) return;

      // Focus inside a popover rendered outside the dialog (a portaled menu or picker) belongs to that
      // popover, so its Tab order is left alone. Only focus with nowhere to go (body or nothing) is
      // pulled back into the dialog.
      const lost = !active || active === document.body;
      if (!lost && !dialog.contains(active)) return;

      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      // The container itself counts as "before the first control", so Shift+Tab from it wraps too.
      if (e.shiftKey && (active === first || active === dialog || lost)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || lost)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', handleTab);
      restoreFocus(opener);
    };
  }, [isOpen, dialogRef]);
}

/**
 * Returns focus to the element that opened a dialog. Some openers are transient (for example a node
 * toolbar that unmounts on mouse leave), so if the opener is no longer in the document, focus goes to
 * the canvas instead of being dropped on <body>.
 */
function restoreFocus(opener: HTMLElement | null): void {
  if (opener?.isConnected) {
    opener.focus?.();
    return;
  }
  const canvas = document.querySelector<HTMLElement>('.react-flow');
  if (!canvas) return;
  if (!canvas.hasAttribute('tabindex')) canvas.setAttribute('tabindex', '-1');
  canvas.focus();
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
  footer?: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '600px',
  footer,
}) => {
  const { theme } = useUIStore();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Close on Escape, but only if this is the top-most open modal.
  useTopmostEscape(isOpen, onClose, dialogRef);

  useDialogFocus(dialogRef, isOpen);

  if (!isOpen) {
    return null;
  }


  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    background: theme === 'dark'
      ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    borderRadius: '12px',
    boxShadow: theme === 'dark'
      ? '0 0 40px rgba(58, 140, 69, 0.3)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    border: theme === 'dark'
      ? '1px solid rgba(58, 140, 69, 0.3)'
      : '1px solid #e5e7eb',
    maxWidth,
    width: '100%',
    maxHeight: '95vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: theme === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 700,
    color: theme === 'dark' ? '#f8fafc' : '#111827',
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: theme === 'dark' ? '#94a3b8' : '#64748b',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
    transition: 'color 0.2s',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: theme === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid #e5e7eb',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    flexShrink: 0,
  };

  return (
    <ModalPortal>
      <div style={overlayStyle} onClick={onClose}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          style={modalStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={headerStyle}>
            <h2 id={titleId} style={titleStyle}>{title}</h2>
            <button
              type="button"
              aria-label="Close"
              style={closeButtonStyle}
              onClick={onClose}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = theme === 'dark' ? '#f8fafc' : '#111827';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = theme === 'dark' ? '#94a3b8' : '#64748b';
              }}
              title="Close (Esc)"
            >
              ×
            </button>
          </div>
          <div style={contentStyle}>
            {children}
          </div>
          {footer && <div style={footerStyle}>{footer}</div>}
        </div>
      </div>
    </ModalPortal>
  );
};
